// src/pages/Community/WeponsVehicleIndex.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./css/ui-shell.css";
import "./css/WeponsVehicleIndex.css";
import ToolbarHeader, { GTA_GAME_TABS } from "./components/ToolbarHeader";
import Modal from "./components/Modal";
import WeponsVehicleForm from "./Weapons/WeponsVehicleForm";
import EmptyState from "./components/EmptyState";
import { useNavigate } from "react-router-dom";
import DetailSidePanel from "./components/DetailSidePanel";
import VehicleDetailPanel from "./Vehicles/VehicleDetailPanel";
import WeaponDetailPanel from "./Weapons/WeaponDetailPanel";


/* ----------------- utils ----------------- */
const canonGameKey = (text) => {
  const s = (text || "").toLowerCase();

  if (/san\s*andreas/.test(s)) return "gta-sa";
  if (/vice\s*city/.test(s)) return "gta-vc";
  if (/gta[\s:_\-\s]*(v|5)\b/.test(s) || /^\s*(v|5)\s*$/.test(s))
    return "gta-5";
  if (/gta[\s:_\-\s]*(iv|4)\b/.test(s)) return "gta-4";
  if (/gta[\s:_\-\s]*(iii|3)\b/.test(s)) return "gta-3";
  if (/gta[\s:_\-\s]*(vi|6)\b/.test(s)) return "gta-6";

  return null;
};

const num = (n) => Number(n).toLocaleString("en-US");
const isNum = (v) => typeof v === "number" && Number.isFinite(v);

/* ----------------- component ----------------- */
export default function WeponsVehicleIndex({ user }) {
  // call hook directly; provide safe default
  const [vehicles, setVehicles] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [loadingVeh, setLoadingVeh] = useState(true);
  const [loadingWeap, setLoadingWeap] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "vehicles"); // vehicles | weapons
  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [selectedKey, setSelectedKey] = useState(searchParams.get("game") || "gta-5"); // game filter
  const [showModal, setShowModal] = useState(false);
  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = isSuperAdmin || user?.role === "admin";
  const canAddAsset = isAdmin;
  const [activeVehicleId, setActiveVehicleId] = useState(null);
  const [activeWeaponId, setActiveWeaponId] = useState(null);

  const navigate = useNavigate();

  // keep last written params to avoid effect loop / over-writing the URL
  const lastParamsRef = useRef({
    tab: searchParams.get("tab") || "",
    q: searchParams.get("q") || "",
    game: searchParams.get("game") || "",
  });

  /* ------------- helper: attach collection listener ------------- */
  // small helper to avoid copy/paste between vehicles/weapons
  const attachCollectionListener = (opts) => {
    const {
      collectionName,
      selectedKey,
      setList,
      setLoading,
      mapDoc,
      userUid,
    } = opts;

    let alive = true;
    let unsub = null;
    const gen = Math.random();
    const base = collection(db, collectionName);

    const attach = (ref, generation) =>
      onSnapshot(
        ref,
        (snap) => {
          if (!alive || generation !== gen) return;
          const rows = snap.docs.map((d) => mapDoc(d.id, d.data()));
          setList(rows);
          setLoading(false);
        },
        (err) => {
          if (!alive || generation !== gen) return;
          setLoading(false);

          const msg = String(err?.message || "");
          const needsFallback =
            selectedKey !== "mine" &&
            (
              msg.includes("FAILED_PRECONDITION") ||
              msg.includes("failed-precondition") ||
              msg.includes("requires an index")
            );


          // retry WITHOUT orderBy to avoid needing composite index
          if (needsFallback) {
            try {
              if (unsub) {
                try {
                  unsub();
                } catch { }
                unsub = null;
              }
              const fallbackRef =
                selectedKey === "all"
                  ? base // no orderBy
                  : query(base, where("gameKey", "==", selectedKey));
              unsub = attach(fallbackRef, generation);
            } catch (e) {
              console.error(`${collectionName} fallback failed:`, e);
            }
            return;
          }

          console.error(`${collectionName} query error:`, err);
        }
      );

    setLoading(true);
    setList([]);

    let firstRef;
    if (selectedKey === "all") {
      firstRef = query(base, orderBy("createdAt", "desc"));
    } else if (selectedKey === "mine") {
      firstRef = userUid
        ? query(base, where("authorUid", "==", userUid))
        : query(base, where("authorUid", "==", "__none__"));
    } else {
      firstRef = query(
        base,
        where("gameKey", "==", selectedKey),
        orderBy("createdAt", "desc")
      );
    }

    unsub = attach(firstRef, gen);

    return () => {
      alive = false;
      if (unsub) {
        try {
          unsub();
        } catch { }
      }
    };
  };

  /* ----------------- Firestore: VEHICLES ----------------- */
  useEffect(() => {
    return attachCollectionListener({
      collectionName: "vehicles",
      selectedKey,
      setList: setVehicles,
      setLoading: setLoadingVeh,
      mapDoc: mapVehicleDoc,
      userUid: user?.uid || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, user?.uid]);

  /* ----------------- Firestore: WEAPONS ----------------- */
  useEffect(() => {
    return attachCollectionListener({
      collectionName: "weapons",
      selectedKey,
      setList: setWeapons,
      setLoading: setLoadingWeap,
      mapDoc: mapWeaponDoc,
      userUid: user?.uid || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, user?.uid]);

  /* ----------------- URL sync (?tab, ?q, ?game) ----------------- */
  useEffect(() => {
    const next = {};
    if (tab) next.tab = tab;
    if (search) next.q = search;
    if (selectedKey && selectedKey !== "all") next.game = selectedKey;

    const prev = lastParamsRef.current;

    const changed =
      (next.tab || "") !== prev.tab ||
      (next.q || "") !== prev.q ||
      (next.game || "") !== prev.game;

    if (changed) {
      setSearchParams(next, { replace: true });
      lastParamsRef.current = {
        tab: next.tab || "",
        q: next.q || "",
        game: next.game || "",
      };
    }
    // depend on the app state only; writing sets lastParamsRef so we don't loop.
  }, [tab, search, selectedKey, setSearchParams]);

  useEffect(() => {
    if (!activeVehicleId && !activeWeaponId) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        setActiveVehicleId(null);
        setActiveWeaponId(null);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeVehicleId, activeWeaponId]);


  /* ----------------- Derived list ----------------- */
  const isLoading = tab === "weapons" ? loadingWeap : loadingVeh;

  const list = useMemo(() => {
    const src = tab === "weapons" ? weapons : vehicles;
    let arr = [...src];

    // Hide private items for everyone except the owner (unless "mine")
    if (selectedKey !== "mine") {
      arr = arr.filter((x) => {
        const ownerUid = x.authorUid ?? null;
        const isOwner = !!user?.uid && ownerUid === user.uid;
        const isPrivate = Boolean(x.isPrivate ?? x.isHidden);
        return !isPrivate || isOwner;
      });
    }

    if (selectedKey === "mine" && user?.uid) {
      arr = arr.filter((x) => x.authorUid === user.uid);
    }
    if (selectedKey !== "all" && selectedKey !== "mine") {
      arr = arr.filter((x) => {
        if (x.gameKey) return x.gameKey === selectedKey;
        const k = canonGameKey(x.game);
        return k === selectedKey;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((x) =>
        [
          x.name,
          ...(x.tags || []),
          x.make || x.manufacturer || "",
          x.class || x.type || "",
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    // Sort newest when broad, alpha when specific for grid stability
    if (selectedKey === "all" || selectedKey === "mine") {
      arr.sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
      );
    } else {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }

    return arr;
  }, [tab, weapons, vehicles, search, selectedKey, user?.uid]);

  const vehicleClassIcon = (item) => {
    const cls = (item.class || "").toLowerCase();
    const tags = (item.tags || []).map(t => t.toLowerCase());

    // Boats
    if (cls.includes("boat") || tags.includes("boat") || tags.includes("boats"))
      return "🚤";

    // Submarines
    if (cls.includes("sub") || tags.includes("submarine"))
      return "🛥️";

    // Helicopters
    if (cls.includes("helicopter") || tags.includes("helicopters"))
      return "🚁";

    // Planes / Jets
    if (cls.includes("plane") || cls.includes("aircraft") || tags.includes("planes"))
      return "✈️";

    // Motorcycles
    if (cls.includes("motorcycle") || tags.includes("motorcycles") || tags.includes("bike"))
      return "🏍️";

    // Trucks / Industrial
    if (cls.includes("truck") || cls.includes("industrial") || tags.includes("utility"))
      return "🚚";

    // Emergency
    if (tags.includes("emergency"))
      return "🚓";

    // Military
    if (tags.includes("military"))
      return "🪖";

    // Default car
    return "🚗";
  };
  const weaponCategoryIcon = (item) => {
    const category = (
      item.category ||
      item.type || ""
    ).toLowerCase();

    const tags = (item.tags || []).map(t => t.toLowerCase());

    if (category.includes("pistol")) return "🔫";
    if (category.includes("smg")) return "💥";
    if (category.includes("assault")) return "🧨";
    if (category.includes("shotgun")) return "🔥";
    if (category.includes("sniper")) return "🎯";
    if (category.includes("melee")) return "🗡️";
    if (category.includes("throw")) return "💣";
    if (category.includes("heavy")) return "🚀";
    if (category.includes("special") || tags.includes("utility")) return "⚡";

    return "❓";
  };

  /* ----------------- render ----------------- */
  return (
    <>
      <div className="wepo-wrap">
        <ToolbarHeader
          children={
            <div className="vw-filter-stack" role="tablist" aria-label="Game & Asset filters">

              {/* 🎮 GAME FILTER ROW */}
              <div className="forum-tabs forum-tabs--games">
                {GTA_GAME_TABS.map(({ key, label }) => {
                  const active = selectedKey === key;
                  return (
                    <span
                      key={key}
                      className={`tab-text ${active ? "active" : ""}`}
                      onClick={() => setSelectedKey(key)}
                      role="tab"
                      aria-selected={active}
                    >
                      {label}
                    </span>
                  );
                })}
              </div>

              {/* 🚗 / 🔫 ASSET FILTER ROW */}
              <div className="forum-tabs forum-tabs--assets">
                <span
                  className={`tab-text ${tab === "vehicles" ? "active" : ""}`}
                  onClick={() => setTab("vehicles")}
                  role="tab"
                  aria-selected={tab === "vehicles"}
                >
                  🚗 Vehicles <span className="tab-chip">{vehicles.length}</span>
                </span>

                <span
                  className={`tab-text ${tab === "weapons" ? "active" : ""}`}
                  onClick={() => setTab("weapons")}
                  role="tab"
                  aria-selected={tab === "weapons"}
                >
                  🔫 Weapons <span className="tab-chip">{weapons.length}</span>
                </span>

                {/* 👑 MY ASSETS */}
                {user && (
                  <span
                    className={`tab-text ${selectedKey === "mine" ? "active" : ""}`}
                    onClick={() => setSelectedKey("mine")}
                    role="tab"
                    aria-selected={selectedKey === "mine"}
                    title="Assets you created"
                  >
                    👑 My Assets
                  </span>
                )}
              </div>


            </div>
          }

          search={search}
          setSearch={setSearch}
          placeholder={`Search ${tab === "weapons" ? "weapons" : "vehicles"}…`}
          user={user}
          onAddMission={canAddAsset ? () => setShowModal(true) : undefined}
          addLabel={canAddAsset ? "＋ Add Asset" : null}
        />

        {/* Gallery */}
        <div
          className={`vw-shell ${activeVehicleId || activeWeaponId ? "panel-open" : ""
            }`}
        >
          <ul className="card-grid sc-gallery vw-gallery">
            {isLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <li key={`sk-${i}`} className="card sc-card vw-card">
                  <div className="sc-thumb vw-thumb skeleton" />
                  <div className="sc-name skeleton skeleton-text" />
                  <div className="sc-subtle vw-subline">
                    <span className="chip skeleton skeleton-chip" />
                    <span className="chip skeleton skeleton-chip" />
                  </div>
                </li>
              ))}

            {!isLoading &&
              list.map((item) => {
                const isPrivate = Boolean(item.isPrivate ?? item.isHidden);
                const visLabel = isPrivate ? "Private" : "Public";
                const visClass = isPrivate ? "is-private is-hidden" : "is-public is-visible";

                const ownerUid = item.authorUid ?? null;
                const isOwner = !!user?.uid && ownerUid === user.uid;

                const gameLabel =
                  GTA_GAME_TABS.find((g) => g.key === item.gameKey)?.label ||
                  (item.gameKey ? item.gameKey : "Game");
                const modeLabel =
                  item.gameMode === "online"
                    ? "Online"
                    : item.gameMode === "story"
                      ? "Story"
                      : null;

                const cardBody = (
                  <>
                    <div className="sc-thumb vw-thumb">
                      <div className="vw-overlay-left">
                        {item.gameMode === "online" && (
                          <span
                            className="vw-mode-icon"
                            title="Online"
                            aria-label="Online"
                          >
                            🌐
                          </span>
                        )}

                        {isOwner && (
                          <span
                            className="vw-owner-inline"
                            title="Your asset"
                            aria-label="Your asset"
                          >
                            👑
                          </span>
                        )}
                      </div>


                      {item.image ? (
                        <img
                          className="sc-img vw-img"
                          src={item.image}
                          alt={`${item.name} ${tab === "weapons" ? "weapon" : "vehicle"}`}
                          loading="lazy"
                        />
                      ) : (
                        <div className="sc-img sc-img--placeholder vw-placeholder">
                          {tab === "weapons"
                            ? weaponCategoryIcon(item)
                            : vehicleClassIcon(item)
                          }
                        </div>
                      )}

                      {Array.isArray(item.tags) && item.tags.length > 0 && (
                        <div className="vw-tags">
                          {item.tags.slice(0, 2).map((t) => (
                            <span className="vw-tag" key={t}>{t}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="vw-row">
                      <span className="vw-badge">{gameLabel}</span>

                      {isOwner && (
                        <span
                          className={`loci-tag ${isPrivate ? "is-hidden" : "is-visible"}`}
                          title="Visibility (only visible to you)"
                        >
                          {isPrivate ? "Private" : "Public"}
                        </span>
                      )}
                    </div>


                    <div className="sc-name vw-title" title={item.name}>
                      {item.name}
                    </div>

                    <div className="sc-subtle vw-subline">
                      {tab === "weapons" ? (
                        <>
                          {item.type && <span className="chip">{item.type}</span>}
                          {item.manufacturer && <span className="chip">{item.manufacturer}</span>}
                          {isNum(item.price) && <span className="chip">${num(item.price)}</span>}
                        </>
                      ) : (
                        <>
                          {item.make && <span className="chip">{item.make}</span>}
                          {item.class && <span className="chip">{item.class}</span>}
                          {isNum(item.price) && <span className="chip">${num(item.price)}</span>}
                        </>
                      )}
                    </div>
                  </>
                );

                return (
                  <li
                    key={item.id}
                    className="card sc-card vw-card"
                    onClick={() => {
                      if (tab === "vehicles") {
                        setActiveWeaponId(null);
                        setActiveVehicleId(item.id);
                      } else {
                        setActiveVehicleId(null);
                        setActiveWeaponId(item.id);
                      }
                    }}
                  >
                    <div className="card-link sc-link">
                      {cardBody}
                    </div>
                  </li>
                );
              })}

            {!isLoading && list.length === 0 && (
              <li className="empty-block">
                <EmptyState
                  icon={tab === "weapons" ? "🔫" : "🚗"}
                  title={`No ${tab === "weapons" ? "Weapons" : "Vehicles"} Available`}
                  description="Please stay tuned. New assets will be added soon."
                />
              </li>
            )}

          </ul>
          <div className="vehicle-sidepanel-host">
            {/* 🚗 Vehicle panel */}
            <DetailSidePanel
              open={!!activeVehicleId}
              title="Vehicle Details"
              onClose={() => setActiveVehicleId(null)}
              onMaximize={() => navigate(`/vehicles/${activeVehicleId}`)}
            >
              <VehicleDetailPanel vehicleId={activeVehicleId} />
            </DetailSidePanel>

            {/* 🔫 Weapon panel */}
            <DetailSidePanel
              open={!!activeWeaponId}
              title="Weapon Details"
              onClose={() => setActiveWeaponId(null)}
              onMaximize={() => navigate(`/weapons/${activeWeaponId}`)}
            >
              <WeaponDetailPanel weaponId={activeWeaponId} />
            </DetailSidePanel>
          </div>
        </div>

        {/* Add Asset Modal */}
        {canAddAsset && (
          <Modal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            title="Add Asset (Weapon / Vehicle)"
            size="lg"
            closeOnBackdrop
            showClose
          >
            <WeponsVehicleForm
              user={user}
              onDone={() => setShowModal(false)}
              onCancel={() => setShowModal(false)}
              defaultGameKey={
                selectedKey && selectedKey !== "all" && selectedKey !== "mine"
                  ? selectedKey
                  : "gta-6"
              }
            />
          </Modal>
        )}

      </div>
    </>
  );
}

/* ----------------- mappers ----------------- */
function mapVehicleDoc(id, x = {}) {
  const image =
    x.coverUrl ||
    x.coverImage ||
    (Array.isArray(x.gallery) && x.gallery.length > 0 ? x.gallery[0] : "") ||
    x.image ||
    x.imageUrl ||
    "";

  return {
    id,
    name: x.name || x.vehicleName || "—",
    image,
    tags: Array.isArray(x.tags) ? x.tags : [],
    make: x.make || x.manufacturer || "",
    class: x.class || "",
    price: isNum(x.price) ? x.price : undefined,
    createdAt: x.createdAt,
    authorUid: x.authorUid || x.createdBy || null,
    gameKey: canonGameKey(x.gameKey || x.game) || x.gameKey || null,
    game: x.game || "",
    gameMode: x.gameMode || "story",
    // visibility (back-compat)
    isPrivate: Boolean(x.isPrivate ?? x.isHidden ?? false),
  };
}

function mapWeaponDoc(id, x = {}) {
  const image =
    x.coverUrl ||
    x.coverImage ||
    (Array.isArray(x.gallery) && x.gallery.length > 0 ? x.gallery[0] : "") ||
    x.image ||
    x.imageUrl ||
    x.portrait ||
    "";

  return {
    id,
    name: x.name || "—",
    image,

    // ✅ THIS WAS MISSING
    category: x.category || "",

    tags: Array.isArray(x.tags) ? x.tags : [],

    // keep type stable for UI labels
    type: x.type || "Weapon",

    manufacturer: x.manufacturer || "",
    price: isNum(x.price) ? x.price : undefined,
    createdAt: x.createdAt,
    authorUid: x.authorUid || x.createdBy || null,
    gameKey: canonGameKey(x.gameKey || x.game) || x.gameKey || null,
    game: x.game || "",
    gameMode: x.gameMode || "story",
    isPrivate: Boolean(x.isPrivate ?? x.isHidden ?? false),
  };
}
